import { BaseTrackingPayload, TrackingEvents } from '@/utils/amplitude/analytics/core.types';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';
import { getDeployment } from '@/redux/modules/config';
import { getFullUrl } from '@/utils/amplitude/analytics/utils/getFullUrl';
import { track } from '@/utils/amplitude/analytics/core';
import { useAppSelector } from '@/redux/hooks';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

type CTAClickedPayload = {
    CTAName?: string;
    bidAmount?: number;
    buttonAction: string;
    eventTrigger?: string;
    index?: number;
    smsNotificationsEnabled?: boolean;
};

type CTAClickedPayloadCombined = CTAClickedPayload & BaseTrackingPayload;

type CTAEventStructure = {
    [page: string]: {
        [event: string]: CTAClickedPayload;
    };
};

export const AnalyticsCTAEvent = {
    CheckoutPage: {
        PayNow: {
            buttonAction: 'Pay Now',
            CTAName: 'Pay Now',
            eventTrigger: 'User clicks "Pay Now" on checkout page',
        },
        SavePaymentMethod: {
            buttonAction: 'Save Payment Method',
            CTAName: 'Save Payment Method',
            eventTrigger: 'User clicks "Save Payment Method" on checkout page',
        },
        SaveShippingAddress: {
            buttonAction: 'Save Shipping Address',
            CTAName: 'Save Shipping Address',
            eventTrigger: 'User clicks "Save Shipping Address" on checkout page',
        },
        SaveShippingMethod: {
            buttonAction: 'Save Shipping Method',
            CTAName: 'Save Shipping Method',
            eventTrigger: 'User clicks "Save Shipping Method" on checkout page',
        },
    },
    HammerHeader: {
        Join: {
            buttonAction: 'Opens the Create an account modal',
            CTAName: 'Join',
            eventTrigger: 'User clicked on the Join CTA',
        },
        Login: {
            buttonAction: 'Opens the login modal',
            CTAName: 'Log In',
            eventTrigger: 'User clicked on the log in CTA',
        },
    },
    HomePage: {
        ShopNow: {
            buttonAction: 'Re-directs user to the star wars collections page',
            CTAName: 'Shop Now',
            eventTrigger: 'User clicks "Shop Now" on marketing campaign banner',
        },
    },
    ItemPage: {
        AuctionTitleInCatalogCarousel: {
            buttonAction: 'Redirects user to the auction catalog page',
            CTAName: 'Auction Title Clicked',
            eventTrigger: 'User clicked on the auction title in the auction catalog carousel',
        },
        RegisterForAuction: {
            buttonAction: 'Starts the auction registration flow',
            CTAName: 'Register for Auction',
            eventTrigger: 'User clicked on the Register for auction link',
        },
        SuggestedBidsForReservePriceChip: {
            buttonAction: 'Shows the suggested max bid chip in the review bid page',
            CTAName: 'Suggested Max Bid Chip',
            eventTrigger: 'User clicked on the suggested max bid chip in the review bid page',
        },
        ViewAuction: {
            buttonAction: 'Redirects user to the auction catalog page',
            CTAName: 'View Auction',
            eventTrigger: 'User clicked on the View Auction link in the auction catalog carousel',
        },
        ViewAuctionPolicy: {
            buttonAction: 'Shows the shipping, payment and auction policies modal',
            CTAName: 'View Auction Policy for Details',
            eventTrigger: 'User clicked on the View Auction Policy for Details link',
        },
        ViewNextLot: {
            buttonAction: 'Shows the next lot in the Catalog',
            CTAName: 'Next Lot',
            eventTrigger: 'User clicked on the view next lot link above the image on the lot details page',
        },
        ViewPrevLot: {
            buttonAction: 'Shows the previous lot in the Catalog',
            CTAName: 'Previous Lot',
            eventTrigger: 'User clicked on the view previous lot link above the image on the lot details page',
        },
    },
    LotDetailPage: {
        SellSimilarItem: {
            buttonAction: 'Initiates selling flow; redirects user to seller intake or listing form',
            CTAName: 'Sell a Similar Item',
            eventTrigger: 'User clicks the "Sell a Similar Item" CTA',
        },
    },
    Notifications: {
        DisableSMSNotifications: {
            buttonAction: 'Disables SMS Notifications',
            CTAName: 'SMS notifications checkbox',
            eventTrigger: 'User unchecks the SMS box on the emails and notifications page',
            smsNotificationsEnabled: false,
        },
        EnableSMSNotifications: {
            buttonAction: 'Enables SMS Notifications',
            CTAName: 'Receive SMS',
            eventTrigger: 'User checks the SMS box on the emails and notifications page',
            smsNotificationsEnabled: true,
        },
    },
    PriceResultsPage: {
        SellSimilarItem: {
            buttonAction: 'Redirects user to seller intake or listing form',
            CTAName: 'Sell a Similar Item',
            eventTrigger: 'User clicks "Sell a Similar Item" on price results page',
        },
        UnlockAllPrices: {
            buttonAction: 'Opens the login modal for logged out users',
            CTAName: 'Unlock All Prices',
            eventTrigger:
                'User clicks on the "unlock all prices" CTA in the Similar Sale History recommendation module',
        },
        UnlockItem: {
            buttonAction: 'Unlocks hidden item details or pricing',
            CTAName: 'Unlock',
            eventTrigger: 'User clicks "Unlock" on price results page',
        },
        ViewItemDetails: {
            buttonAction: 'Navigates user to Lot Details Page',
            CTAName: 'View Item Details',
            eventTrigger: 'User clicks "View Item Details" on price results page',
        },
    },
} satisfies CTAEventStructure;

export const trackCTAClickedAnalytics = (payload: CTAClickedPayloadCombined) => {
    track(TrackingEvents.CTA.CTAClicked, payload);
};

export const useTrackCTAClickedAnalytics = () => {
    const deployment = useAppSelector(getDeployment);
    const { pathname } = useLocation();

    return useCallback(
        ({
            buttonAction,
            CTAName,
            eventTrigger,
            ...rest
        }: Omit<CTAClickedPayloadCombined, 'pageName' | 'url' | 'page'>) => {
            trackCTAClickedAnalytics({
                buttonAction,
                CTAName,
                eventTrigger,
                pageName: getAmplitudePageNameFromPath(pathname),
                url: getFullUrl(deployment, pathname),
                // There might be additional params
                ...rest,
            });
        },
        [deployment, pathname]
    );
};
